import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Play, 
  ArrowLeft, 
  Shield, 
  Server, 
  Activity, 
  Bell, 
  FileText, 
  Settings,
  CheckCircle2,
  Clock,
  Users,
  Zap,
  ChevronRight
} from "lucide-react";

const features = [
  {
    icon: Server,
    title: "Real-time Server Monitoring",
    description: "Monitor CPU, memory, disk, and network metrics across all your servers in real-time.",
    timestamp: "0:00"
  },
  {
    icon: Activity,
    title: "Windows Event Log Collection",
    description: "Automatically collect and analyze Windows event logs for security insights.",
    timestamp: "1:30"
  },
  {
    icon: Shield,
    title: "Security Compliance",
    description: "Track compliance status and identify security vulnerabilities instantly.",
    timestamp: "3:00"
  },
  {
    icon: Bell,
    title: "Smart Alerting",
    description: "Configure custom alerts for critical events with Slack and email notifications.",
    timestamp: "4:30"
  },
  {
    icon: FileText,
    title: "Audit Logging",
    description: "Comprehensive audit trails for all system and user activities.",
    timestamp: "5:45"
  },
  {
    icon: Settings,
    title: "Easy Agent Setup",
    description: "One-command installation with secure token-based authentication.",
    timestamp: "6:30"
  }
];

const stats = [
  { label: "Servers Monitored", value: "10,000+", icon: Server },
  { label: "Events Processed", value: "1B+", icon: Activity },
  { label: "Active Users", value: "500+", icon: Users },
  { label: "Uptime", value: "99.99%", icon: Zap }
];

export default function DemoPage() {
  const navigate = useNavigate();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  // Replace with your actual YouTube video ID
  const youtubeVideoId = "dQw4w9WgXcQ"; // Placeholder - replace with actual demo video ID

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <Button 
              onClick={() => navigate("/")}
              className="bg-primary hover:bg-primary/90"
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <Badge variant="secondary" className="mb-6 bg-primary/20 text-primary border-primary/30">
              <Play className="w-3 h-3 mr-1" />
              Product Demo
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              See ServSpect in Action
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Watch our comprehensive demo to discover how ServSpect can transform your server monitoring 
              and security compliance workflow.
            </p>
          </div>

          {/* Video Container */}
          <div className="relative max-w-5xl mx-auto animate-scale-in">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary/20 border border-white/10">
              {/* Video Thumbnail Overlay */}
              {!isVideoPlaying && (
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center cursor-pointer group z-10"
                  onClick={() => setIsVideoPlaying(true)}
                >
                  <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920')] bg-cover bg-center opacity-30" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                  
                  {/* Play Button */}
                  <div className="relative z-10 flex flex-col items-center gap-6">
                    <div className="w-24 h-24 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 group-hover:bg-primary transition-all duration-300 shadow-lg shadow-primary/50">
                      <Play className="w-10 h-10 text-white fill-white ml-1" />
                    </div>
                    <div className="text-center">
                      <p className="text-white text-xl font-semibold">Watch the Full Demo</p>
                      <p className="text-white/60 flex items-center gap-2 justify-center mt-2">
                        <Clock className="w-4 h-4" />
                        8 minutes
                      </p>
                    </div>
                  </div>

                  {/* Corner badges */}
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Badge className="bg-red-500/90 text-white border-0">HD Quality</Badge>
                    <Badge className="bg-white/10 text-white border-white/20">2024 Update</Badge>
                  </div>
                </div>
              )}

              {/* YouTube Embed */}
              <div className="aspect-video bg-slate-900">
                {isVideoPlaying && (
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&modestbranding=1`}
                    title="ServSpect Demo Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                )}
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-y border-white/10 bg-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Chapters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What You'll Learn
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Jump to any section of the demo to see specific features in action.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="bg-white/5 border-white/10 hover:bg-white/10 hover:border-primary/50 transition-all duration-300 cursor-pointer group"
                onClick={() => setIsVideoPlaying(true)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-colors">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-white group-hover:text-primary transition-colors">
                          {feature.title}
                        </h3>
                        <Badge variant="outline" className="text-xs border-white/20 text-white/60">
                          {feature.timestamp}
                        </Badge>
                      </div>
                      <p className="text-sm text-white/60">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-primary/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose ServSpect?
            </h2>
          </div>

          <div className="space-y-4">
            {[
              "Enterprise-grade security with end-to-end encryption",
              "Deploy agents in under 5 minutes with single command setup",
              "Real-time monitoring with sub-second metric collection",
              "Comprehensive Windows event log analysis",
              "SOC 2, HIPAA, and GDPR compliance ready",
              "24/7 automated security monitoring and alerting"
            ].map((benefit, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10 hover:border-primary/30 transition-colors"
              >
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-white">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/70 mb-8">
            Join hundreds of companies already using ServSpect to secure their infrastructure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate("/")}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg"
            >
              Start Free Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate("/security")}
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              <Shield className="w-5 h-5 mr-2" />
              Learn About Security
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © 2024 ServSpect. All rights reserved.
          </p>
          <div className="flex gap-6">
            <button 
              onClick={() => navigate("/privacy")} 
              className="text-white/40 hover:text-white text-sm transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => navigate("/terms")} 
              className="text-white/40 hover:text-white text-sm transition-colors"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => navigate("/security")} 
              className="text-white/40 hover:text-white text-sm transition-colors"
            >
              Security
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}